import { checkNavPermission, ROUTES } from '@futrli/shared-services';
import { WorkspaceMember } from '@futrli-sdk/people';
import { WorkspacesListingItem } from '@futrli-sdk/workspaces';

import { getUserName } from '../../utils/users/getUserName';
import { USER_ROLE_IDS } from './constants';

export interface UserRowActions {
  delete: boolean;
  edit: boolean;
}

export interface UserRowPermissions {
  canChangeOrganisations: boolean;
  canEditRole: boolean;
}

export interface ProcessedUserRow extends WorkspaceMember {
  actions: UserRowActions;
  displayName: string;
  isMfaEnabled: boolean;
  organisationCount: number;
  permissions: UserRowPermissions;
  status: UserRowStatus;
}

export type UserRowStatus = 'active' | 'pending';

interface ProcessUserRowsCurrentUser {
  id: string;
}

const USER_ROLE_SCORES: Record<string, number> = {
  [USER_ROLE_IDS.SUPER_ADMIN]: 5,
  [USER_ROLE_IDS.ADMINISTRATOR]: 4,
  [USER_ROLE_IDS.PEOPLE_MANAGER]: 3,
  [USER_ROLE_IDS.DATA_MANAGER]: 2,
  [USER_ROLE_IDS.GUEST]: 1
};

const USER_MANAGER_ROLE_IDS: string[] = [
  USER_ROLE_IDS.SUPER_ADMIN,
  USER_ROLE_IDS.ADMINISTRATOR,
  USER_ROLE_IDS.PEOPLE_MANAGER
];

/**
 * Returns whether the user belongs to an organisation whose membership is managed by an external system.
 */
const hasExternallyManagedOrganisation = (user: WorkspaceMember, workspace: WorkspacesListingItem): boolean =>
  user.organisations.some(memberOrganisation => {
    const organisation = workspace.organisations.find(item => item.id === memberOrganisation.id);

    return organisation ? organisation.kind !== 'demo' && organisation.billed_by !== 'chargebee' : false;
  });

/**
 * Returns whether the acting user's role is equal to or higher than the target user's role.
 */
const hasEqualOrHigherRole = (actor: WorkspaceMember, target: WorkspaceMember): boolean =>
  (USER_ROLE_SCORES[actor.role.id] ?? 0) >= (USER_ROLE_SCORES[target.role.id] ?? 0);

/**
 * Returns whether the acting user can edit the target user's role or delete them from the workspace.
 *
 * Managers cannot manage themselves, the workspace owner, higher-role users, or externally managed users.
 */
const canEditUserRoleOrDelete = (
  actor: WorkspaceMember | undefined,
  target: WorkspaceMember,
  currentUser: ProcessUserRowsCurrentUser,
  workspace: WorkspacesListingItem
): boolean => {
  if (!actor || !USER_MANAGER_ROLE_IDS.includes(actor.role.id)) {
    return false;
  }

  if (currentUser.id === target.id || workspace.owner.id === target.id) {
    return false;
  }

  if (hasExternallyManagedOrganisation(target, workspace)) {
    return false;
  }

  return hasEqualOrHigherRole(actor, target);
};

/**
 * Returns the organisation count shown in the users list.
 * Super Administrators implicitly have access to every organisation in the workspace.
 */
const getOrganisationCount = (user: WorkspaceMember, workspace: WorkspacesListingItem): number =>
  user.role.id === USER_ROLE_IDS.SUPER_ADMIN ? workspace.organisations.length : user.organisations.length;

/**
 * Converts workspace members into display-ready rows with their available actions and field-level permissions.
 *
 * The workspace `members` permission is required for every mutation. Organisation assignments remain editable for
 * any authenticated workspace member with that permission, while role editing and deletion apply the legacy role,
 * ownership, hierarchy, self-management, and external-organisation restrictions.
 *
 * @param users All members of the selected workspace.
 * @param currentUser The currently authenticated user.
 * @param workspace The selected workspace and its permissions.
 * @returns Processed rows for the users list and Modify User sidebar.
 */
export const processUserRows = (
  users: WorkspaceMember[],
  currentUser: ProcessUserRowsCurrentUser,
  workspace: WorkspacesListingItem
): ProcessedUserRow[] => {
  const currentMember = users.find(user => user.id === currentUser.id);
  const hasMembersPermission = checkNavPermission(workspace.permissions, ROUTES.MEMBERS_ROUTE);

  return users.map(user => {
    const canEditRoleAndDelete =
      hasMembersPermission && canEditUserRoleOrDelete(currentMember, user, currentUser, workspace);
    const canChangeOrganisations = hasMembersPermission && !!currentMember;
    const canEdit =
      canEditRoleAndDelete ||
      (canChangeOrganisations && user.role.id !== USER_ROLE_IDS.SUPER_ADMIN);

    return {
      ...user,
      actions: {
        delete: canEditRoleAndDelete,
        edit: canEdit
      },
      displayName: getUserName(user.name),
      isMfaEnabled: user.mfa_status === 'sms',
      organisationCount: getOrganisationCount(user, workspace),
      permissions: {
        canChangeOrganisations,
        canEditRole: canEditRoleAndDelete
      },
      status: user.invitation?.status === 'pending' ? 'pending' : 'active'
    };
  });
};
